import React from 'react';

export enum OrderDirection {
  ASC = 'ASC',
  DESC = 'DESC',
}

export const INVERSE_ORDER = {
  [OrderDirection.ASC]: OrderDirection.DESC,
  [OrderDirection.DESC]: OrderDirection.ASC,
};

type SortField<T> = string & T;

type Order = OrderDirection.ASC | OrderDirection.DESC | undefined;

type UseSortAndOrderApi<F extends Parameters> = {
  getHandleChangeArgument: (field: SortField<F['sort']>) => () => void;
  getOrderArgument: (field: SortField<F['sort']>) => Order;
};

type SetFilterFunc<F extends Parameters> = (vals: F) => void;

const negateDirection = (v: Order): Order => {
  switch (v) {
    case OrderDirection.ASC:
      return OrderDirection.DESC;
    case OrderDirection.DESC:
      return OrderDirection.ASC;
    default:
      return OrderDirection.DESC;
  }
};

type Parameters = {
  order?: string;
  sort?: string;
};

const useSortAndOrder = <F extends Parameters>(parameters: F, setFilter: SetFilterFunc<F>): UseSortAndOrderApi<F> => {
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line no-restricted-syntax
  const getHandleChangeArgument = React.useCallback<UseSortAndOrderApi<F>['getHandleChangeArgument']>(
    (sort: SortField<F['sort']>) => {
      const currentOrder: Order = parameters.order as Order;
      const currentSort: SortField<F['sort']> = parameters.sort as SortField<F['sort']>;
      const newOrder = negateDirection(currentSort === sort ? currentOrder : undefined);
      return () =>
        setFilter({
          order: newOrder,
          sort,
        } as F);
    },
    [parameters, setFilter],
  );

  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line no-restricted-syntax
  const getOrderArgument = React.useCallback<UseSortAndOrderApi<F>['getOrderArgument']>(
    (sort: SortField<F['sort']>) => {
      const currentOrder: Order = parameters.order as Order;
      const currentSort: SortField<F['sort']> = parameters.sort as SortField<F['sort']>;
      return currentSort === sort ? currentOrder : undefined;
    },
    [parameters],
  );

  return React.useMemo<UseSortAndOrderApi<F>>(
    () => ({
      getHandleChangeArgument,
      getOrderArgument,
    }),
    [getHandleChangeArgument, getOrderArgument],
  );
};

export default useSortAndOrder;
